import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
} from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { JwtAuthModule, StorageType } from '@devlearning/jwt-auth';
import { MutexFastLockModule } from '@devlearning/mutex-fast-lock';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AccordionModule } from 'primeng/accordion';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { ToastModule } from 'primeng/toast';
import { Observable, catchError, exhaustMap, map, of, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { MessageComponent } from './core/components/message/message.component';
import { GlobalErrorHandler } from './core/global-error-handler';
import { LanguageCultureInterceptor } from './core/interceptors/language-culture.interceptor';
import { EnumsService } from './core/services/enums.service';
import { LanguageApiService } from './core/services/language-api.service';
import { UserService } from './core/services/user.service';
import { ApiService } from './core/services/api.service';
import { AuthApplicationCodes } from './core/models/auth-application-code';

@NgModule({
  declarations: [AppComponent, MessageComponent],
  imports: [
    BrowserModule,
    AccordionModule,
    ConfirmDialogModule,
    ToastModule,
    DynamicDialogModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      defaultLanguage: environment.DEFAULT_LANGUAGE,
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    MutexFastLockModule.forRoot({
      lockPrefix: '_MUTEX_LOCK',
      timeout: 30000,
      debugEnabled: false,
    }),
    JwtAuthModule.forRoot({
      logLevel: 1,
      tokenUrl: environment.TOKEN_URL,
      refreshUrl: environment.REFRESH_URL,
      useManualInitialization: true,
      storageType: StorageType.SESSION_STORAGE,
      refreshTokenRequestFactory: (token) => ({
        authApplicationCode: AuthApplicationCodes.Sales,
        username: token.username,
        refreshToken: token.refreshToken,
      }),
    }),
    FontAwesomeModule,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppFactory,
      multi: true,
      deps: [LanguageApiService, UserService, EnumsService, ApiService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageCultureInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

function initializeAppFactory(
  language: LanguageApiService,
  user: UserService,
  enums: EnumsService,
  api: ApiService,
): () => Observable<any> {
  return () =>
    user.initialize().pipe(
      exhaustMap((x) =>
        api.LanguageCulture_List().pipe(
          map(
            (lc) =>
              lc.items?.find(
                (l) => l.idLanguageCulture === x.userData?.language.idLanguageCulture,
              ) ?? null,
          ),
          catchError((err) => {
            console.error('Failed to load language cultures:', err);
            return of(null);
          }),
        ),
      ),
      switchMap((lc) => language.initialize(lc)),
      exhaustMap((x) => enums.initialize()),
      map((x) => true),
      catchError((err) => {
        console.error('App initialization failed:', err);
        return of(true);
      }),
    );
}
